import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  Cartesian2,
  Cartesian3,
  Matrix3,
  Matrix4,
  Transforms,
  Color,
  createGuid,
  HeadingPitchRoll,
  Quaternion,
  Model,
  Cesium3DTileFeature,
  Cesium3DTileset,
  EllipsoidTerrainProvider,
  SceneTransforms,
  Primitive,
  GeometryInstance,
  PolylineGeometry,
  PolylineMaterialAppearance,
  Material,
  Entity,
  CallbackProperty,
  BoundingSphere,
  Math as CesiumMath,
} from "@cesium/engine";
import EditRotate from "../edit/EditRotate";
import DitapViewer from "../basic/DitapViewer";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";
import PointEntity from "../edit/PointEntity";
import LinestringEntity from "../edit/LinestringEntity";
import PolygonEntity from "../edit/PolygonEntity";

/**
 *
 * @private
 * @param {*} selectFeature
 * @returns {string}
 */
const getSelectFeatureId = (selectFeature) => {
  let id;
  if (typeof selectFeature?.id === "string") {
    id = selectFeature.id;
  } else if (selectFeature?.id instanceof Entity) {
    id = selectFeature.id.id;
  }

  return id;
};

/**
 * Interaction 클래스에서 지원하는 object 추출
 */
const getSelectObject = (viewer, selectFeature) => {
  let selectObject;
  if (defined(selectFeature)) {
    // DitapStorage 체크
    const id = getSelectFeatureId(selectFeature);
    const ditapStorageInstance = viewer.ditapStorage.getInstanceById(id);
    if (ditapStorageInstance) {
      if (
        ditapStorageInstance instanceof PointEntity ||
        ditapStorageInstance instanceof LinestringEntity ||
        ditapStorageInstance instanceof PolygonEntity
      ) {
        selectObject = ditapStorageInstance;
      } else {
        return;
      }
    }

    // DitapStorage 데이터가 아닌 경우
    // Scale에서 Entity는 지원하지 않음
    if (!selectObject) {
      if (selectFeature?.id instanceof Entity) {
        selectObject = selectFeature.id;
      } else if (selectFeature?.primitive?.id instanceof Entity) {
        selectObject = selectFeature.primitive.id;
      } else if (selectFeature instanceof Cesium3DTileFeature) {
        selectObject = selectFeature.tileset;
      } else if (selectFeature?.primitive instanceof Cesium3DTileset) {
        selectObject = selectFeature.primitive;
      } else if (selectFeature?.primitive instanceof Model) {
        selectObject = selectFeature.primitive;
      }
    }
  }

  return selectObject;
};

/**
 * 객체 회전을 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input (mouse/keyboard) class for object rotation.
 * @alias InteractionRotate
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {function} [callback=undefined] 콜백 함수.</br> Save callback function.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = Ditap.Model.fromGltf({
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 실행
 * const interactionRotate = new Ditap.InteractionRotate(viewer);
 * interactionRotate.on();
 */
class InteractionRotate {
  constructor(viewer, callback) {
    this._viewer = undefined; // DitapViewer
    this._handler = undefined; // 핸들러
    this._cameraRotate = undefined; // 카메라 이동 가능 유무
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      this._handler = new ScreenSpaceEventHandler(viewer.canvas);
      this._cameraRotate =
        viewer.scene.screenSpaceCameraController.enableRotate;
    } else {
      throw Error("InteractionRotate viewer type error");
    }

    this._callback = undefined;
    if (callback instanceof Function) {
      this._callback = callback;
    } else if (callback === undefined) {
      this._callback = undefined;
    } else {
      throw Error("InteractionRotate callback type error");
    }

    this._isOn = false; // on 상태 확인
    this._isLeftDown = false; // 마우스 좌클릭중인지

    this._axis = undefined; // 컨트롤 엔티티
    this._selectAxis = undefined; // 선택중인 축
    this._selectAxisName = undefined;
    this._selectObject = undefined;

    this._mousePosition = undefined;
    this._originalPosition = undefined;

    this._rotation = undefined;
    this._quaternion = undefined;
    this._originalRotation = undefined;
    this._originalQuaternion = undefined;

    this._keyDownEvent = undefined;
    this._infoLabelEntity = undefined;
    this._buttonElement = undefined;
    this._offAlert = undefined;
    this._instantAlert = undefined;
    this._isMobile = undefined; // 모바일 여부
    this.cancelBtn = undefined;
    this.endBtn = undefined;
    this.cancelEvent = undefined;
    this.endEvent = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /*
   * 축 엔티티.<br>
   * Array of axis entities.
   * @type {Entity[]}
   */
  get axis() {
    return this._axis;
  }
  set axis(axis) {
    this._axis = axis;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @readonly
   * @type {boolean}
   */
  get isOn() {
    return this._isOn;
  }
  // set isOn(isOn) {
  //   this._isOn = isOn;
  // }

  /*
   * 마우스 왼 클릭 여부.<br>
   * Mouse left down state.
   * @type {boolean}
   */
  get isLeftDown() {
    return this._isLeftDown;
  }
  set isLeftDown(isLeftDown) {
    this._isLeftDown = isLeftDown;
  }

  /*
   * 카메라 회전 여부.<br>
   * Camera rotation state.
   * @type {boolean}
   */
  get cameraRotate() {
    return this._cameraRotate;
  }
  set cameraRotate(cameraRotate) {
    this._cameraRotate = cameraRotate;
  }

  /*
   * 선택된 축 엔티티.<br>
   * Select axis entity.
   * @type {Entity}
   */
  get selectAxis() {
    return this._selectAxis;
  }
  set selectAxis(selectAxis) {
    this._selectAxis = selectAxis;
  }

  /*
   * 선택된 축 이름.<br>
   * Select axis name.
   * @type {Entity}
   */
  get selectAxisName() {
    return this._selectAxisName;
  }
  set selectAxisName(selectAxisName) {
    this._selectAxisName = selectAxisName;
  }

  /*
   * 선택된 객체.<br>
   * Select Object.
   * @type {Model | Cesium3DTileset}
   */
  get selectObject() {
    return this._selectObject;
  }
  set selectObject(selectObject) {
    this._selectObject = selectObject;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  get mousePosition() {
    return this._mousePosition;
  }
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 정보를 제공 라벨 요소.</br>
   * Label that provide information.
   * @type {Element}
   */
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /**
   * 종료 매서드 콜백함수.<br>
   * Off method callback.
   * @type {Function | undefined}
   */
  get callback() {
    return this._callback;
  }
  set callback(callback) {
    if (callback instanceof Function || callback instanceof undefined) {
      this._callback = callback;
    }
  }

  /**
   * 적용된 회전값.<br>
   * Applied rotation value.
   * @readonly
   * @type {Quaternion}
   */
  get rotation() {
    return this._rotation;
  }

  /*
   * 종료 시 알림창.<br>
   * off method alert
   * @type {DitapAlert}
   */
  get offAlert() {
    return this._offAlert;
  }
  set offAlert(offAlert) {
    this._offAlert = offAlert;
  }

  /*
   * 모델의 원래 위치.<br>
   * The original position of the model.
   * @type {Cartesian3}
   */
  get originalPosition() {
    return this._originalPosition;
  }

  /*
   * 모델 되돌리기.<br>
   * model rollback.
   * @returns {Model | Cesium3DTileset | undefined} 적용된 모델.<br> Model in interaction.
   */
  rollback() {
    const selectObject = this.selectObject;

    if (selectObject) {
      this._rotation = this._originalRotation;
      this._quaternion = this._originalQuaternion;

      if (
        selectObject instanceof Entity ||
        selectObject instanceof PointEntity ||
        selectObject instanceof LinestringEntity ||
        selectObject instanceof PolygonEntity
      ) {
        EditRotate.setRotation(selectObject, this._originalQuaternion);
      } else {
        EditRotate.setRotation(selectObject, this._originalRotation);
      }

      this.updateAxis();
    }

    return selectObject;
  }

  /*
   * Ditap Viewer에 폴리라인 엔티티 생성.</br>
   * Create a polylineEntity in the DitapViewer.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {string} id 폴리라인 아이디.<br> Polyline id
   * @param {Cartesian3[]} positions 폴리라인 생성 좌표 배열.</br> Cartesians of polylineEntity.
   * @param {Color} [color=Color.RED] 생성할 폴리라인 색상.<br> Polyline color
   * @param {number} [width=3] 생성할 폴리라인 너비.<br> Polyline width
   * @returns {Entity} 생성된 폴리라인 엔티티. </br> Created polylineEntity.
   */
  createPolyline(viewer, id, positions, color = Color.RED, width = 3) {
    // const polyline = viewer.entities.add({
    //   id: id,
    //   name: name,
    //   polyline: {
    //     positions: positions,
    //     width: width,
    //     material: color,
    //     depthFailMaterial: Color.GRAY,
    //   },
    // });

    const polyline = viewer.scene.primitives.add(
      new Primitive({
        //# geomtry 설정
        geometryInstances: new GeometryInstance({
          id: id || createGuid(), //ID
          geometry: new PolylineGeometry({
            //position, width
            positions: positions,
            width: width || 5,
          }),
        }),
        //# 입력된 geomretryInstances 생상 설정
        appearance: new PolylineMaterialAppearance({
          material: new Material({
            fabric: {
              type: "Color", //fabric 타입에 따라 polyline 모양이 바뀜
              uniforms: {
                color: color,
              },
            },
          }),
        }),
        //# 입력된 geometryInstance가 depthfail인 경우 Alpha 색상 적용
        depthFailAppearance: new PolylineMaterialAppearance({
          material: new Material({
            fabric: {
              type: "Color",
              uniforms: {
                color: color.withAlpha(0.35),
                //color: color
              },
            },
          }),
        }),
        asynchronous: false, //비동기 해제
        //modelMatrix: matrix
      })
    );

    return polyline;
  }

  /*
   * 대상 위치의 좌우 방향을 오프셋에서 계산.<br>
   * Calculate the left and right direction of the target position from offset
   * @param {Cartesian3} offset 창의 위치.<br> Offset position
   * @param {Cartesian3} forward 창의 현재 방향.<br> Forward vector
   * @param {Cartesian3} target 목표 지점.<br> Target position
   * @returns {number} 결과 값(왼쪽 방향: 음수, 오른쪽 방향: 양수).<br> The result value(value on the left, + on the right).
   */
  calculateWindowDirection(offset, forward, target) {
    const forwardVector = Cartesian2.subtract(
      forward,
      offset,
      new Cartesian3()
    );
    const targetVector = Cartesian2.subtract(target, offset, new Cartesian3());
    const up_vector = new Cartesian3(0, 0, 1);

    const cross = Cartesian3.cross(
      forwardVector,
      targetVector,
      new Cartesian3()
    );
    let dot = Cartesian3.dot(up_vector, cross, new Cartesian3());

    dot = dot / Math.abs(dot);

    return dot;
  }

  /*
   * 모델의 방향 계산.<br>
   * Calculate the model's orientation.
   * @param {Cartesian3} position 모델의 중심 포인트.<br> Center point of the model
   * @param {Cartesian3[]} vector 선택된 축의 벡터.<br> Vector of the selected axis
   * @param {Matrix4} matrix 모델 매트릭스.<br> Model matirx
   * @returns {number} 결과 값(왼쪽 방향: 음수, 오른쪽 방향: 양수).<br> The result value(value on the left, + on the right).
   */
  calculate3DTileDirection(position, vector, matrix) {
    const scene = this.viewer.scene;

    // if (this.pt && this.pt[0] instanceof Entity) {
    //   this.viewer.entities.remove(this.pt[0]);
    // }
    // if (this.pt && this.pt[1] instanceof Entity) {
    //   this.viewer.entities.remove(this.pt[1]);
    // }
    // this.pt = [];

    // Axis의 0°, 90° window 좌표
    const positions = [];
    for (const i of [0, 90]) {
      const radians = CesiumMath.toRadians(i);
      const a = Cartesian3.add(
        position,
        Cartesian3.multiplyByScalar(
          vector[0],
          50 * Math.cos(radians),
          new Cartesian3()
        ),
        new Cartesian3()
      );
      const sum = Cartesian3.add(
        a,
        Cartesian3.multiplyByScalar(
          vector[1],
          -50 * Math.sin(radians),
          new Cartesian3()
        ),
        new Cartesian3()
      );

      const model = Matrix4.multiplyByPoint(matrix, sum, new Cartesian3());

      positions.push(scene.cartesianToCanvasCoordinates(model));

      // this.pt.push(
      //   this.viewer.entities.add({
      //     id: createGuid(),
      //     position: model,
      //     point: {
      //       color: i === 0 ? Color.YELLOW : Color.PURPLE,
      //       pixelSize: 20,
      //     },
      //   })
      // );
    }

    // Cesium3DTileset 중심점의 window 좌표
    const centerToWindow = scene.cartesianToCanvasCoordinates(position);

    // Cesium3DTilset 중심점 <-> ENU 간의 좌우 구분
    const dot = this.calculateWindowDirection(
      centerToWindow,
      positions[0],
      positions[1]
    );

    return dot;
  }

  /*
   * 입력 좌표의 X,Y,Z 벡터를 계산.<br>
   * Compute eastnorthup XYZ vector of input coordinates.
   * @param {Cartesian3} position 계산할 기준점.<br> Calculation base point.
   * @param {string | undefined} [axis=undefined] 축(X, Y, Z). 정의되지 않은 경우 모든 축 벡터 반환.</br> Axis is 'X', 'Y', 'Z'. When the undefined, return all axis vector.
   * @return {Cartesian3 | Cartesian3[]} 계산된 축 벡터.<br> Axis vector.
   */
  getAxisVector(position, axis = undefined) {
    // Vector 추출
    // axis vector
    const modelMatrix = Transforms.eastNorthUpToFixedFrame(position); //# NorthEastUp 변환
    const matrix3 = Matrix4.getMatrix3(modelMatrix, new Matrix3()); //# XYZ 벡터 추출
    const vector = [
      Matrix3.getColumn(matrix3, 0, new Cartesian3()),
      Matrix3.getColumn(matrix3, 1, new Cartesian3()),
      Matrix3.getColumn(matrix3, 2, new Cartesian3()),
    ];

    // 결과값
    let result;
    if (axis === "X") {
      result = vector[0];
    } else if (axis === "Y") {
      result = vector[1];
    } else if (axis === "Z") {
      result = vector[2];
    } else {
      result = vector;
    }

    return result;
  }

  /*
   * 축 엔티티 생성.<br>
   * Create axis entities
   * @param {Cartesian3} position 축 생성 좌표.<br> Axis creation coordinates.
   * @param {number} radius 반지름.<br> Radius.
   * @returns {Entity[]} 생성된 축 엔티티 배열.</br> [X axis entity, Y axis entity, Z axis entity]
   */
  createAxis(position, radius) {
    // center position
    //const position = this.originalPosition;
    // East-North-Up vector from center position
    const vector = this.getAxisVector(position, undefined);
    // axis length
    //const radius = object.boundingSphere.radius;

    /*
     * 원을 구성하는 좌표 계산
     * @private
     * @param {Cartesian3} vector
     * @param {Cartesian3} position
     * @param {number} radius
     * @returns {Cartesian3[]}
     */
    const computeCircle = (vector, position, radius) => {
      const positions = [];
      for (let i = 0; i <= 360; i += 9) {
        // Angle -> Radian
        const radians = CesiumMath.toRadians(i);
        const cosAngle = Math.cos(radians);
        const sinAngle = Math.sin(radians);

        // 벡터를 스칼라로 조정하여 반지름에 맞는 위치 벡터를 계산
        const scaledVector0 = Cartesian3.multiplyByScalar(
          vector[0],
          cosAngle * radius,
          new Cartesian3()
        );
        const scaledVector1 = Cartesian3.multiplyByScalar(
          vector[1],
          sinAngle * radius,
          new Cartesian3()
        );

        // 두 벡터를 합하여 최종 위치 벡터 계산
        const sum = Cartesian3.add(
          scaledVector0,
          scaledVector1,
          new Cartesian3()
        );

        // 원의 중심 위치 + 최종 위치 벡터 = 원 좌표 값
        const circlePoint = Cartesian3.add(position, sum, new Cartesian3());

        positions.push(circlePoint);
      }

      return positions;
    };

    // x axis info 12
    const guid = createGuid();
    const x_axis = [
      `InteractionRotate_X_AXIS_${guid}`, // id
      [Color.RED, 12], // color, width
      [vector[1], vector[2]],
    ];
    // y axis info 02
    const y_axis = [
      `InteractionRotate_Y_AXIS_${guid}`,
      [Color.GREEN, 12],
      [vector[0], vector[2]],
    ];
    // z axis info 01
    const z_axis = [
      `InteractionRotate_Z_AXIS_${guid}`,
      [Color.BLUE, 12],
      [vector[0], vector[1]],
    ];

    // create axis
    const axis_list = [];
    for (const axis of [x_axis, y_axis, z_axis]) {
      // object의 행렬 변화에 따라 axis 위치가 변하도록 해줌
      const pos = computeCircle(axis[2], position, radius);

      // axis 생성
      axis_list.push(
        this.createPolyline(
          this.viewer,
          axis[0], // id
          pos, // positions
          axis[1][0], // color
          axis[1][1] // polyline width
        )
      );
    }

    return axis_list;
  }

  /*
   * 축 엔티티 제거.<br>
   * Remove axis entities
   */
  removeAxis() {
    const viewer = this.viewer;
    const axis = this.axis;

    for (const entity of axis) {
      //viewer.entities.remove(entity);
      viewer.scene.primitives.remove(entity);
    }

    this.axis = undefined;
    this.selectAxis = undefined;
    this.selectAxisName = undefined;
  }

  computeMatrix(position, translation, rotation, scale) {
    //# ENU -> ECEF convert value
    const toGlobal = Transforms.eastNorthUpToFixedFrame(position);
    const inverse = Matrix4.inverse(toGlobal, new Matrix4());

    //# combine matrix => scale x rotation
    const matrix = Matrix4.fromTranslationQuaternionRotationScale(
      Cartesian3.ZERO,
      rotation,
      scale,
      new Matrix4()
    );

    //# compute transform
    const transform = Matrix4.multiply(
      toGlobal,
      Matrix4.multiply(matrix, inverse, new Matrix4()),
      new Matrix4()
    );

    //# set translation
    Matrix4.setTranslation(
      transform,
      new Cartesian3(
        transform[12] + translation.x,
        transform[13] + translation.y,
        transform[14] + translation.z
      ),
      transform
    );

    return transform;
  }

  /*
   * 축 매트릭스 수정.<br>
   * Update axis matrix.
   */
  updateAxis() {
    const axisList = this.axis;
    if (axisList) {
      // 모델 중심 점 기준 East-North-Up vector 생성
      const position = this.originalPosition;
      const toGlobal = Transforms.eastNorthUpToFixedFrame(position); //1. 중심점 -> EastNorthUp Matrix4 변환
      const inverse = Matrix4.inverse(toGlobal, new Matrix4()); //2. Inverse 계산

      // 현재 모델의 회전값, 스케일 추출
      //const { quaternion } = this.editRotate.decompositionMatrix(object);
      const quaternion = this.rotation;

      // rotation은 translation=0, scale=1 고정
      const matrix = Matrix4.fromTranslationQuaternionRotationScale(
        Cartesian3.ZERO,
        quaternion,
        Cartesian3.ONE,
        new Matrix4()
      );

      // 변환
      const transform = Matrix4.multiply(
        toGlobal,
        Matrix4.multiply(matrix, inverse, new Matrix4()),
        new Matrix4()
      );

      // axis 행렬 업데이트
      for (const axis of axisList) {
        axis.modelMatrix = transform;
      }
    }
  }

  /*
   * 마우스 위치를 기반으로 각도 계산.<br>
   * Calculate angle based on mouse position.
   * @param {Cartesian3} position 기준점.<br> Datum point.
   * @param {Quaternion} quaternion 기준쿼터니언.<br> Datum quaternion.
   * @param {Cartesian2} startWindowPosition 마우스 시작 지점의 창 좌표.<br> Mouse starting point window coordinates.
   * @param {Cartesian2} endWindowPosition 마우스 종료 지점의 창 좌표.<br> Mouse endpoint window coordinates.
   * @param {string} axis 선택 축(X, Y, Z).<br> Select axis ["X", "Y", "Z"].
   * @returns {Cartesian3} 이동 값.<br> Movement value.
   */
  calculateMovementValue(
    position,
    quaternion,
    startWindowPosition,
    endWindowPosition,
    selectAxis
  ) {
    const viewer = this.viewer;
    // const { position, quaternion } = this.editRotate.decompositionMatrix(
    //   selectObject
    // );

    const centerToWindow = viewer.scene.cartesianToCanvasCoordinates(position); //# 3DTile 중심점 window position

    const forwardVector = Cartesian2.subtract(
      startWindowPosition,
      centerToWindow,
      new Cartesian2()
    ); //# 처음 클릭한 위치의 window position <-> 3DTile 중심점 window position
    const targetVector = Cartesian2.subtract(
      endWindowPosition,
      centerToWindow,
      new Cartesian2()
    ); //# 마우스 window position <-> 3DTile 중심점 window position

    let distance = Cartesian2.angleBetween(forwardVector, targetVector); //# 이전 각도 - 현재 각도

    const windowDirection = this.calculateWindowDirection(
      centerToWindow,
      startWindowPosition,
      endWindowPosition
    ); //# 적용해야 하는 각도가 + or - 인지 계산

    //# undefined인 경우 무시
    if (isNaN(windowDirection)) {
      return;
    }
    distance *= windowDirection;

    //# set 3DTile direction => 현재 3D 타일 [0, 90]의 좌 우 구분
    let vector = this.getAxisVector(position);
    switch (selectAxis) {
      case "X":
        vector = [vector[1], vector[2], vector[0]];
        break;
      case "Y":
        vector = [vector[2], vector[0], vector[1]];
        break;
      case "Z":
        vector = [vector[0], vector[1], vector[2]];
        break;
      default:
        break;
    }

    const matrix = this.computeMatrix(
      position,
      Cartesian3.ZERO,
      quaternion,
      Cartesian3.ONE
    );
    // if (selectObject instanceof Cesium3DTileset) {
    //   //matrix = selectObject.modelMatrix;
    //   matrix = this.editRotate.calculateTilesetMatrix(
    //     position,
    //     position,
    //     quaternion,
    //     Cartesian3.ONE
    //   );
    // } else if (selectObject instanceof Model) {
    //   // const decompositionMatrix = this.editRotate.decompositionMatrix(
    //   //   selectObject
    //   // );
    //   // const inverse = Matrix4.inverse(
    //   //   Transforms.eastNorthUpToFixedFrame(decompositionMatrix.translation),
    //   //   new Matrix4()
    //   // );
    //   // const inverseMatirx = this.editRotate.removeByEpsilon(
    //   //   Matrix4.multiply(selectObject.modelMatrix, inverse, new Matrix4())
    //   // );
    //   // matrix = inverseMatirx;
    //   matrix = this.editRotate.calculateTilesetMatrix(
    //     position,
    //     position,
    //     quaternion,
    //     Cartesian3.ONE
    //   );
    // } else if (selectObject instanceof Entity) {
    //   matrix = this.editRotate.calculateTilesetMatrix(
    //     position,
    //     position,
    //     quaternion,
    //     Cartesian3.ONE
    //   );
    // } else {
    //   return;
    // }

    let tilesetDirection = this.calculate3DTileDirection(
      position,
      vector,
      matrix
    );

    // ENU => X -1
    if (selectAxis === "X") {
      tilesetDirection *= -1;
    }

    //# undefined인 경우 무시
    if (isNaN(tilesetDirection)) {
      return;
    }
    distance *= tilesetDirection;

    //# set HeadingPitchRoll
    let hpr;
    if (selectAxis === "X") {
      hpr = new HeadingPitchRoll(0, 0, distance);
    } else if (selectAxis === "Y") {
      hpr = new HeadingPitchRoll(0, distance, 0);
    } else if (selectAxis === "Z") {
      hpr = new HeadingPitchRoll(distance, 0, 0);
    }

    return hpr;
  }

  entityOrientationToENUQuaternion(position, quaternion) {
    const toGlobal = Transforms.eastNorthUpToFixedFrame(position); //1. 중심점 -> EastNorthUp Matrix4 변환
    const inverse = Matrix4.inverse(toGlobal, new Matrix4()); //2. Inverse 계산

    // rotation은 translation=0, scale=1 고정
    const matrix = Matrix4.fromTranslationQuaternionRotationScale(
      Cartesian3.ZERO,
      quaternion,
      Cartesian3.ONE,
      new Matrix4()
    );

    const rotation = Quaternion.fromRotationMatrix(
      Matrix4.getRotation(
        Matrix4.multiply(inverse, matrix, new Matrix4()),
        new Matrix3()
      ),
      new Quaternion()
    );

    return rotation;
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      // axis 제거
      // if (this.selectObject) {
      //   if (this.axis) {
      //     this.removeAxis();
      //   }
      //   this.selectObject = undefined;
      // }

      if (this.selectObject) {
        this.off();
      } else {
        this.off(false);
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    // const getPrimitive = (selectPrimitive, primitives) => {
    //   const length = primitives.length;
    //   for (let i = 0; i < length; ++i) {
    //     const primitive = primitives.get(i);
    //     if (primitive instanceof Cesium.PrimitiveCollection) {
    //       return getPrimitive(selectPrimitive, primitive);
    //     } else {
    //       if (primitive === selectPrimitive) {
    //         return primitive;
    //       }
    //     }
    //   }
    // };

    handler.setInputAction((e) => {
      const selectFeature = viewer.scene.pick(e.position);
      if (!this.selectObject && selectFeature) {
        const selectObject = getSelectObject(viewer, selectFeature);
        if (selectObject) {
          this.selectObject = selectObject;

          let radius;
          // Entity
          if (this.selectObject instanceof Entity) {
            // 기준점 지정
            const position = Utils.getPropertyValue(this.selectObject.position);
            if (!position) {
              this._instantAlert.open();
            }
            this._originalPosition = position.clone();
            this._position = position;

            // BoundingSphere 추출
            const boundingSphere = new BoundingSphere();
            viewer.dataSourceDisplay.getBoundingSphere(
              this.selectObject,
              true,
              boundingSphere
            );
            radius = boundingSphere.radius;

            // Quaternion, ENU-Quaternion
            if (this.selectObject.orientation) {
              this._quaternion = EditRotate.getQuaternion(
                this.selectObject,
                false
              );
            } else {
              this._quaternion = Transforms.headingPitchRollQuaternion(
                position,
                new HeadingPitchRoll(0, 0, 0)
              );
            }
            this._originalQuaternion = this._quaternion.clone();
            this._rotation = this.entityOrientationToENUQuaternion(
              position,
              this._quaternion
            );
            this._originalRotation = this._rotation.clone();

            // Entity position CallbackProperty 설정
            EditRotate.setRotation(
              this.selectObject,
              new CallbackProperty(() => {
                return this._quaternion;
              }, false)
            );

            // DitapGeometry
          } else if (
            this.selectObject instanceof PointEntity ||
            this.selectObject instanceof LinestringEntity ||
            this.selectObject instanceof PolygonEntity
          ) {
            const boundingSphere = this.selectObject.computeBoundingSphere();
            if (!boundingSphere) {
              this._instantAlert.open();
            }
            this._originalPosition = boundingSphere.center.clone();
            this._position = boundingSphere.center;

            // Axis 크기
            radius = boundingSphere.radius;

            // Quaternion, ENU-Quaternion
            if (this.selectObject.orientation) {
              this._quaternion = EditRotate.getQuaternion(
                this.selectObject,
                false
              );
            } else {
              this._quaternion = Transforms.headingPitchRollQuaternion(
                this._position,
                new HeadingPitchRoll(0, 0, 0)
              );
            }
            this._originalQuaternion = this._quaternion.clone();
            this._rotation = this.entityOrientationToENUQuaternion(
              this._position,
              this._quaternion
            );
            this._originalRotation = this._rotation.clone();

            // Entity position CallbackProperty 설정
            EditRotate.setRotation(
              this.selectObject,
              new CallbackProperty(() => {
                return this._quaternion;
              }, false)
            );

            // Primitive
          } else {
            // BoundingSphere 추출
            // 기준점 지정
            const boundingSphere = this.selectObject.boundingSphere;
            if (!boundingSphere) {
              this._instantAlert.open();
            }
            this._originalPosition = boundingSphere.center.clone();
            this._position = boundingSphere.center;

            // Axis 크기
            radius = boundingSphere.radius;

            const decomposition = EditRotate.decomposite(this.selectObject);
            this._quaternion = Transforms.headingPitchRollQuaternion(
              boundingSphere.center,
              decomposition.headingPitchRoll
            );
            this._originalQuaternion = this._quaternion.clone();
            this._rotation = decomposition.quaternion;
            this._originalRotation = this._rotation.clone();
          }

          // 기준점 없으면 예외처리
          if (!defined(this._originalPosition) || !defined(this._position)) {
            this._instantAlert.open();
          }

          // AXIS 생성
          this.axis = this.createAxis(
            this._originalPosition,
            radius <= 0 ? 1000 : radius
          );
          this.updateAxis();

          // 인포 라벨 제거
          if (!this._isMobile) {
            // 인포 라벨 제거
            this.infoLabelEntity.remove();
          }

          if (this.endBtn) {
            this.endBtn.style.display = "flex";
          }
          if (this.cancelBtn) {
            this.cancelBtn.style.display = "none";
          }
        } else {
          this._instantAlert.open();
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 왼쪽 버튼 누를 경우 실행 이벤트 생성.</br>
   * Mouse left down cesium handler.
   */
  createLeftDownHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const pickedFeature = viewer.scene.pick(e.position);

      let selectAxis;
      let selectAxisName;
      if (
        pickedFeature &&
        pickedFeature.primitive &&
        pickedFeature.primitive instanceof Primitive
      ) {
        if (this.axis?.includes(pickedFeature.primitive)) {
          this.isLeftDown = true;
          viewer.scene.screenSpaceCameraController.enableRotate = false;

          selectAxis = pickedFeature.primitive;
          selectAxisName = pickedFeature.id.split("_")[1];
        }
      }

      this.selectAxis = selectAxis;
      this.selectAxisName = selectAxisName;
    }, ScreenSpaceEventType.LEFT_DOWN);
  }

  /*
   * 마우스 왼쪽 버튼을 뗄 경우 실행 이벤트 생성.</br>
   * Mouse left up cesium handler.
   */
  createLeftUpHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.isLeftDown = false;
      this.selectAxis = undefined;
      this.selectAxisName = undefined;
      viewer.scene.screenSpaceCameraController.enableRotate = this._cameraRotate;
    }, ScreenSpaceEventType.LEFT_UP);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const isLeftDown = this.isLeftDown;
      const selectAxis = this.selectAxis;
      const selectObject = this.selectObject;
      const selectAxisName = this.selectAxisName;

      if (selectObject) {
        if (isLeftDown) {
          if (selectAxis) {
            const movementValue = this.calculateMovementValue(
              this._position,
              this.rotation,
              e.startPosition,
              e.endPosition,
              selectAxisName
            );
            if (movementValue) {
              // 총 회전량
              const newRotation = Quaternion.multiply(
                this._rotation,
                Quaternion.fromHeadingPitchRoll(movementValue),
                new Quaternion()
              );
              this._rotation = newRotation;

              // 적용된 Quaternion
              const newQuaternion = Quaternion.multiply(
                this._quaternion,
                Quaternion.fromHeadingPitchRoll(movementValue),
                new Quaternion()
              );
              this._quaternion = newQuaternion;

              // 모델 업데이트
              if (
                !(
                  selectObject instanceof Entity ||
                  selectObject instanceof PointEntity ||
                  selectObject instanceof LinestringEntity ||
                  selectObject instanceof PolygonEntity
                )
              ) {
                EditRotate.setRotation(selectObject, newRotation);
              }

              // AXIS 업데이트
              this.updateAxis();
            } else {
              //console.error("InteractionRotate movementValue undefined");
            }
          } else {
            //console.error("InteractionRotate Move selectAxis undefined");
          }
        }
      }

      if (!this._isMobile && !selectObject) {
        // 마우스 좌표 설정
        const position = this.pickPosition(e.endPosition, viewer);
        if (defined(position)) {
          this.mousePosition = position;

          // 도움말 라벨 좌표 이동
          const entity_pos = SceneTransforms.wgs84ToWindowCoordinates(
            viewer.scene,
            this.mousePosition
          );

          this.infoLabelEntity.classList.remove("hide");
          if (entity_pos.x > 0 && entity_pos.y > 0) {
            this.infoLabelEntity.style.left = `${Number(entity_pos.x + 20)}px`;
            this.infoLabelEntity.style.top = `${Number(entity_pos.y + 20)}px`;
            this.infoLabelEntity.style.right = "unset";
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /**
   * 객체 회전 핸들러 실행. (EditRotate 초기화 필수) <br>
   * Execute object rotation handler. (EditRotate initialization is required.)
   */
  on() {
    if (!this._isOn) {
      this.viewer.currentEvent = this;
      this._isMobile = Utils.detectDeviceType();
      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this._isOn = !this._isOn ? true : false;
      this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

      this.offAlert = new DitapAlert({
        type: "question",
        title: "편집 종료",
        content1: "확인 : 편집 적용",
        content2: "취소 : 편집 초기화",
        buttons: "OKANDCANCEL",
        open: false,
        callback: () => {
          if (this._callback instanceof Function) {
            try {
              this.callback(this._quaternion);
            } catch (e) {
              this.rollback();
            }
          }
          this._offAlert.close();
          this._selectObject = undefined;
          this._scale = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._quaternion = undefined;
          this._rotation = undefined;
          this._originalRotation = undefined;
          this._originalQuaternion = undefined;
        },
        closeCallback: () => {
          this.rollback();
          this._offAlert.close();
          this._selectObject = undefined;
          this._translation = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._quaternion = undefined;
          this._rotation = undefined;
          this._originalRotation = undefined;
          this._originalQuaternion = undefined;
        },
      });

      this._instantAlert = new DitapAlert({
        type: "info",
        title: "회전 편집",
        content1: "해당 객체는 회전 편집을",
        content2: "지원하지 않습니다.",
        buttons: "ONLYOK",
        open: false,
      });

      // 경고창 - X 버튼 이벤트 => 콜백 바인딩 해제
      const oldElement = this._offAlert.closeBtn;
      const newElement = oldElement.cloneNode(true);
      newElement.addEventListener("click", () => {
        this._offAlert.close();
      });
      this._offAlert.closeBtn.replaceWith(newElement);

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여",
        "회전 모델을 선택해주세요",
        true
      );

      // 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 회전 모델을 선택해주세요"
      );

      if (this._isMobile) {
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
        this.cancelBtn.style.display = this.selectObject ? "none" : "flex";
        this.endBtn.style.display = this.selectObject ? "flex" : "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createLeftClickHandler();
      this.createLeftDownHandler();
      this.createLeftUpHandler();
      this.createResizeEvent();
      this.createMouseMoveHandler();
      //this.createKeyDownHandler();
    }
  }

  /**
   * 객체 회전 핸들러 종료. (EditRotate 초기화 필수.)<br>
   * End object rotation handler. (EditRotate initialization is required.)
   * @param {boolean} [alert=true] 종료 팝업 오픈 여부.<br> alert If true, pops up the exit popup.
   * @param {function} [callback=undefined] 팝업을 통해 실행 될 콜백 함수.<br> function at the end of interaction via popup.
   */
  off(alert = true, callback = undefined) {
    if (this._isOn) {
      const offInteraction = () => {
        // Entity에 설정한 CallbackProperty 해제
        if (
          this.selectObject instanceof Entity ||
          this.selectObject instanceof PointEntity ||
          this.selectObject instanceof LinestringEntity ||
          this.selectObject instanceof PolygonEntity
        ) {
          EditRotate.setRotation(this.selectObject, this._quaternion);
        }

        if (this.axis) {
          this.removeAxis();
        }
        // 버튼 비활성화
        this.buttonElement?.classList.remove("on");

        // 도움말 라벨 제거.
        this.mobileInfoLabel?.label.remove();
        this.infoLabel?.remove();

        if (!this.handler?.isDestroyed()) {
          this.handler?.destroy();
        }

        window.removeEventListener("keydown", this.keyDownEvent);

        if (this._isMobile) {
          this.removeMobileEvent();
        }

        if (this.resizeEvent) {
          window.removeEventListener("resize", this.resizeEvent);
        }

        this.handler = undefined;
        this._isOn = false;
        this.selectObject = undefined;
        this.selectAxis = undefined;
        this.mousePosition = undefined;
        this._originalPosition = undefined;
        this._rotation = undefined;
        this._originalRotation = undefined;
        this._quaternion = undefined;
        this._originalQuaternion = undefined;
        this.mobileInfoLabel = undefined;
        this.infoLabel = undefined;
        this.infoLabelEntity = undefined;

        this.viewer.currentEvent = undefined;
        this.cancelBtn = undefined;
        this.endBtn = undefined;
        this._isMobile = undefined;
      };

      // 알림창 없이 종료
      if (!this.selectObject) {
        // Interaction 종료
        offInteraction();
        // 알림창 open
      } else if (alert && this.selectObject) {
        // Right Click ContextMenu 제거
        const contextMenu = document.oncontextmenu;
        document.oncontextmenu = (e) => {
          e.preventDefault();
        };

        this.offAlert.open();
        this.offAlert.callback = (alert) => {
          // Interaction 종료
          offInteraction();

          // Right Click ContextMenu 복구
          document.oncontextmenu = contextMenu;
          if (callback) {
            callback();
          }
          alert.destroy();
        };
        this.offAlert.closeCallback = (alert) => {
          this.rollback();
          this._selectObject = undefined;
          this._translation = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._quaternion = undefined;
          offInteraction();
          if (callback) {
            callback();
          }
          alert.destroy();
        };
      }
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this?.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        if (this.selectObject) {
          this.off();
        } else {
          this.off(false);
        }
      })
    );

    this?.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        if (this.selectObject) {
          this.off();
        } else {
          this.off(false);
        }
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.endEvent) {
      this?.endBtn.removeEventListener("click", this.endEvent);
    }
    this.endEvent = undefined;
    this.endBtn = undefined;

    if (this.cancelEvent) {
      this?.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";
            this.cancelBtn.style.display = this.selectObject ? "none" : "flex";
            this.endBtn.style.display = this.selectObject ? "flex" : "none";

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    if (this.axis) {
      this.removeAxis();
    }
    this.handler.destroy();
  }

  /*
   * Terrain provider 여부에 따라 새로운 Cartesian 반환.</br>
   * Returns new cartesian based on ellipsoid terrain provider presence.
   * @param {Cartesian3} position 재구성할 Cartesian 좌표.</br> The cartesian to be reconstructed.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Cartesian3} 재구성된 Cartesian 좌표. </br> Reconstructed based on ellipsoid terrain provider presence.
   */
  pickPosition(position, viewer) {
    let newPosition;
    let isEllipsoid = false;
    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      newPosition = viewer.scene.pickPosition(position);
    } else {
      newPosition = viewer.camera.pickEllipsoid(
        position,
        viewer.scene.globe.ellipsoid
      );
    }

    return newPosition;
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    // ESC : 체적 측정 실행 취소
    if (event.key === "Escape") {
      //this.off();
      //this.destroyInstance();
      if (this.selectObject) {
        this.off();
      } else {
        this.off(false);
      }
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}

export default InteractionRotate;
